import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { useIsReducedMotion } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	ReducedMotionRovoGeneratingAnimation,
	RovoGeneratingAnimation,
} from '../../../ai-answer-dialog/ai-answer-loading/rovo-generating-animation';
import { onAIAnswerLoadingDialogShown } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { messages } from '../../smart-answer-card/messages';

import RovoIcon from './assets/Rovo.svg';

const styles = cssMap({
	containerNewSAINLoading: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		height: '198px',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	inline: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingTop: token('space.100'),
	},
	inlineAnimation: {
		display: 'inline-flex',
		verticalAlign: 'middle',
	},
	jiraNlToJqlCardHeightOverride: {
		height: '382px',
	},
	navigationalCardHeightOverride: {
		height: '280px',
	},
	luminaOverride: {
		height: '214px',
		paddingTop: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},
	borderlessNoPadding: {
		height: '160px',
		paddingTop: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
	},
	luminaTallHeightOverride: {
		height: '352px',
		paddingTop: token('space.0'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
	},
	sourcesSidePanelOverride: {
		height: '199px',
	},
	sourcesFullWidthOverride: {
		height: '187px',
		overflow: 'hidden',
	},
	skeletonContent: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: '0px',
	},
	skeletonRailOffset: {
		marginTop: token('space.negative.200'),
		paddingTop: token('space.100'),
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'start',
	},
	inlineCompact: {
		paddingBottom: token('space.0'),
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'start',
	},
});

const SKELETON_HEIGHT_LINE = '16px';
const SKELETON_BORDER_RADIUS = '6px';

type SkeletonLineConfig = { width: string; height: string };

/** Standard SAIN: 3 lines (two full-width, one 90%). */
const skeletonLinesStandard: SkeletonLineConfig[] = [
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '90%', height: SKELETON_HEIGHT_LINE },
];

/** Borderless UX: 3 lines (two full-width, one 75%). */
const skeletonLinesBorderless: SkeletonLineConfig[] = [
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '100%', height: SKELETON_HEIGHT_LINE },
	{ width: '75%', height: SKELETON_HEIGHT_LINE },
];

/** Full-width right rail: 3 shorter lines (two 85%, one 65%). */
const skeletonLinesRightRail: SkeletonLineConfig[] = [
	{ width: '90%', height: SKELETON_HEIGHT_LINE },
	{ width: '90%', height: SKELETON_HEIGHT_LINE },
	{ width: '70%', height: SKELETON_HEIGHT_LINE },
];

function getSkeletonLines(isBorderlessUX: boolean, hasRightRail: boolean): SkeletonLineConfig[] {
	if (hasRightRail) {
		return skeletonLinesRightRail;
	}
	return isBorderlessUX ? skeletonLinesBorderless : skeletonLinesStandard;
}

type SmartAnswerCardBodyGeneratingProps = {
	heightOverride?: 'jiraNlToJqlCard' | 'navigationalCard' | 'lumina';
	rightRail?: React.ReactNode;
};
export function SmartAnswerCardBodyGenerating({
	heightOverride,
	rightRail,
}: SmartAnswerCardBodyGeneratingProps): React.JSX.Element {
	const { commonAttributes, queryReasoning } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const isReducedMotion = useIsReducedMotion();

	const normalizeReasoningString = (str: string): string => {
		if (!str) {
			return str;
		}
		let result = `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
		// Remove trailing period if it exists
		if (result.endsWith('.')) {
			result = result.slice(0, -1);
		}
		return result;
	};

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack
			xcss={cx(
				styles.containerNewSAINLoading,
				heightOverride === 'jiraNlToJqlCard' && styles.jiraNlToJqlCardHeightOverride,
				heightOverride === 'navigationalCard' && styles.navigationalCardHeightOverride,
				heightOverride === 'lumina' &&
					(fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()) &&
					styles.luminaOverride,
				!heightOverride &&
					(fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()) &&
					styles.borderlessNoPadding,
				!heightOverride &&
					SAINFeatureControl.isSAINUXUpliftEnabled() &&
					styles.sourcesSidePanelOverride,
				!heightOverride &&
					SAINFeatureControl.isSourcesFullWidthEnabled() &&
					styles.sourcesFullWidthOverride,
				heightOverride === 'lumina' &&
					SAINFeatureControl.isSAINUXUpliftEnabled() &&
					styles.luminaTallHeightOverride,
			)}
			space="space.200"
		>
			<Inline
				space={'space.100'}
				xcss={rightRail ? styles.inlineCompact : styles.inline}
				alignBlock="start"
			>
				<Image src={RovoIcon} alt="" />
				<Text color={'color.text.subtle'}>
					{queryReasoning ? (
						<FormattedMessage
							{...messages.smart_answer_card_rovo_generating}
							values={{ queryReasoning: normalizeReasoningString(queryReasoning) }}
						/>
					) : (
						<FormattedMessage {...messages.smart_answer_card_rovo_is_thinking} />
					)}
					<Box as="span" xcss={styles.inlineAnimation}>
						{isReducedMotion ? (
							<ReducedMotionRovoGeneratingAnimation />
						) : (
							<RovoGeneratingAnimation />
						)}
					</Box>
				</Text>
			</Inline>
			{rightRail ? (
				<Box xcss={styles.skeletonRailOffset}>
					<Stack space="space.200" xcss={styles.skeletonContent}>
						{getSkeletonLines(SAINFeatureControl.isSAINUXUpliftEnabled(), true).map(
							(line, index) => (
								<Skeleton
									key={index}
									width={line.width}
									height={line.height}
									color={token('color.skeleton.subtle')}
									borderRadius={SKELETON_BORDER_RADIUS}
								/>
							),
						)}
					</Stack>
					{rightRail}
				</Box>
			) : (
				getSkeletonLines(SAINFeatureControl.isSAINUXUpliftEnabled(), false).map((line, index) => (
					<Skeleton
						key={index}
						width={line.width}
						height={line.height}
						color={token('color.skeleton.subtle')}
						borderRadius={SKELETON_BORDER_RADIUS}
					/>
				))
			)}
			{heightOverride === 'lumina' && SAINFeatureControl.isSAINUXUpliftEnabled() && (
				<Inline space="space.100">
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={SKELETON_BORDER_RADIUS}
					/>
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={SKELETON_BORDER_RADIUS}
					/>
					<Skeleton
						width="256px"
						height="166px"
						color={token('color.skeleton.subtle')}
						borderRadius={SKELETON_BORDER_RADIUS}
					/>
				</Inline>
			)}
		</Stack>
	);
}
